import { useEffect } from 'react';

import {
  GATEWAYS_HEALTH_POLLING_INTERVAL_MS,
  useGatewaysHealthActions,
  useGatewaysHealthData,
} from '@/console/features/gateways/hooks/use-gateway-health/gateways-health-context';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadGatewayHealthStatusQuery } from '@/shared/redux/api/assets/gateways/gateways-api';

export const useGatewayHealth = (gatewayId?: string) => {
  const actions = useGatewaysHealthActions();
  const data = useGatewaysHealthData();
  const useProvider = actions !== null;

  useEffect(() => {
    if (!actions || !gatewayId) return;
    actions.register(gatewayId);
    return () => actions.unregister(gatewayId);
  }, [actions, gatewayId]);

  // currentData, not data: `data` reuses the previous args' result, so a reused
  // component instance would report the previous gateway's health as this one's.
  const { currentData: fallbackData } = usePollingQuery({
    useQueryHook: useReadGatewayHealthStatusQuery,
    queryArg: {
      gatewayId,
    },
    pollingInterval: GATEWAYS_HEALTH_POLLING_INTERVAL_MS,
    queryOptions: { skip: !gatewayId || useProvider },
  });

  // Either way, an answer we don't have yet reads as loading rather than as a
  // gateway with no reported health.
  if (useProvider) {
    const entry = gatewayId ? data?.states[gatewayId] : undefined;
    return {
      state: entry?.state,
      reportedDate: entry?.reportedAt,
      isLoading: !!gatewayId && (data?.isPendingFor(gatewayId) ?? false),
    };
  }

  return {
    state: fallbackData?.readGatewayState?.state,
    reportedDate: fallbackData?.readGatewayState?.reportedAt,
    isLoading: !!gatewayId && !fallbackData,
  };
};
